import { Divider, message } from 'antd'
import { useEffect, useState } from 'react'
import Head from '../../components/Head'
import Input from '../../components/UI/Input'
import SelectInput from '../../components/UI/SelectInput'
import DateInput from '../../components/UI/DateInput'
import Button from '../../components/UI/Button'
import { useNavigate, useParams } from 'react-router-dom'
import { createOrUpdateDeviceVersions, getDeviceVersionById } from '../../services/deviceVersions'
import React from 'react'
import SwitchInput from '../../components/UI/SwitchInput'
import { notificationError } from '../../services/notification'
import {onlyNumbersAndDots, onlySpaces} from '../../utils/formValidation'
import Radio from '../../components/UI/Radio'
import { findRelativeStatus } from '../../utils/objectUtils'
import CancelButton from '../../components/CancelButton'
import { numbersAndSpecialCharacters } from '../../utils/stringUtils'
import { DateFormatEnum } from '../../enums/dateFormatEnum'
import dayjs from "dayjs";

interface NewDeviceVersionForm {
  deviceVersion: string;
  deviceType: string;
  validThrough:  dayjs.Dayjs | undefined;
  deviceActivationEnabled: string;
}

function NewDeviceVersion() {
  const {id} = useParams()
  const history = useNavigate()
  const typeOptions = [
    {label: 'POS', value: 'POS'},
    {label: 'GAME', value: 'GAME'},
  ]

  const activationOptions = [
    {label: 'Yes', value: 'yes'},
    {label: 'No', value: 'no'},
  ]

  const posBundleServerEnabledOptions = {
    check: 'Yes',
    uncheck: 'No',
  }

  const [form, setForm] = useState<NewDeviceVersionForm>({
    deviceVersion: "",
    deviceType: typeOptions[0].value,
    validThrough: undefined,
    deviceActivationEnabled: activationOptions[0].value,
  })
  const [orig, setOrig] = useState<NewDeviceVersionForm>({
    deviceVersion: "",
    deviceType: typeOptions[0].value,
    validThrough: undefined,
    deviceActivationEnabled: activationOptions[0].value,
  })
  const [bundlesOnPos, setBundlesOnPos] = useState(posBundleServerEnabledOptions.uncheck)
  const [origBundlesOnPos, setOrigBundlesOnPos] = useState(posBundleServerEnabledOptions.uncheck)
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    (async () => {
      if(id) {
        const response = await getDeviceVersionById({id: id})
        if(!response.deviceVersion) return
        setForm({
          deviceVersion: response.deviceVersion,
          deviceType: findRelativeStatus(response.deviceType, typeOptions),
          validThrough: dayjs(response.validThrough).isValid() ? dayjs(response.validThrough) : undefined,
          deviceActivationEnabled: response.deviceActivationEnabled ? activationOptions[0].value : activationOptions[1].value,
        })
        setOrig({
          deviceVersion: response.deviceVersion,
          deviceType: findRelativeStatus(response.deviceType, typeOptions),
          validThrough: dayjs(response.validThrough).isValid() ? dayjs(response.validThrough) : undefined,
          deviceActivationEnabled: response.deviceActivationEnabled ? activationOptions[0].value : activationOptions[1].value,
        })
        setBundlesOnPos(response.posBundleServerEnabled ? posBundleServerEnabledOptions.check : posBundleServerEnabledOptions.uncheck)
        setOrigBundlesOnPos(response.posBundleServerEnabled ? posBundleServerEnabledOptions.check : posBundleServerEnabledOptions.uncheck)
      }
      setIsInitialized(true)
    })()
  },[])

  useEffect(() => {
    setForm(form)
  },[isInitialized, form])

  const [isDesktop, setDesktop] = useState(window.innerWidth > 739);

  const updateMedia = () => {
    setDesktop(window.innerWidth > 739);
  };

  useEffect(() => {
    window.addEventListener("resize", updateMedia);
    return () => window.removeEventListener("resize", updateMedia);
  });

  const isChanged = () => {

    if(!form.deviceVersion
        || !form.deviceType
        || !form.deviceActivationEnabled
    ) return false
    if(form.deviceVersion!==orig.deviceVersion) return true
    if(!form.validThrough && orig.validThrough) return true
    if(form.validThrough && !orig.validThrough) return true
    if(form.validThrough && !form.validThrough?.isSame(orig.validThrough)) return true
    if(form.deviceActivationEnabled!==orig.deviceActivationEnabled) return true
    if(bundlesOnPos!==origBundlesOnPos) return true
    return form.deviceType !== orig.deviceType;

  }
  const submit = async () => {
    if(!form.deviceVersion || onlySpaces(form.deviceVersion)) return notificationError('Please provide a valid device version')
    if(!onlyNumbersAndDots(form.deviceVersion)) return notificationError('Device version must not contain special characters.')
    const validThrough = form.validThrough &&  dayjs(form.validThrough).format(DateFormatEnum.YEAR_MONTH_DAY)
    const payload = {
      ...form,
      posBundleServerEnabled: bundlesOnPos,
      validThrough: dayjs(form.validThrough).isValid() ? validThrough : undefined
    }

    if(!id) await createOrUpdateDeviceVersions(payload)
    if(id) await createOrUpdateDeviceVersions({id, ...payload})
    history(-1)
  }

  const isBundlesOnPosDisabled = () => {
    let result = form.deviceVersion.split('.');
    if(!form.deviceVersion || form.deviceType !== typeOptions[1].value || !result || result.length < 3) {
      if(bundlesOnPos !== posBundleServerEnabledOptions.uncheck) setBundlesOnPos(posBundleServerEnabledOptions.uncheck)
      return true
    } 
    return false
  }

  return (
    <div className="new-device-version">
      <Head title="New Device Version"/>
      <div className="new-device-version__container">
        <table>
          <tbody>
            <tr>
              <td width={'25%'}>
                <div className="new-device-version__container__row">

                  <Radio
                    label="Type"
                    options={typeOptions}
                    value={form.deviceType}
                    disabled={!!id}
                    onChange={(e: any) => setForm({...form, deviceType: e.target.value})}
                  />
                  {isInitialized && (
                    <DateInput
                      label="Valid Through"
                      onChange={(e: any) => setForm({...form, validThrough: e})}
                      value={form.validThrough}
                    />
                  )}
                </div>
              </td>
            </tr>
            <tr>
              <td width={"25%"}>
                <Input
                    placeholder="Device Version"
                    disabled={!!id}
                    label="Device Version"
                    onChange={(e: any) => setForm({...form, deviceVersion: numbersAndSpecialCharacters(e.target.value)})}
                    value={form.deviceVersion}
                />
              </td>
              {isDesktop && <td>
                Examples: (1) Includes all 1.* version: 1, 1.0, 1.2.3 and so on, but not 2.* (1.1) Includes all 1.1.* version: 1.1, 1.1.3, 1.1.5.7 and so on, but not 2.* or 1.2.*
              </td>}
            </tr>
            <tr>
              <td width={"25%"}>
                <SwitchInput
                    label="Offer bundles on POS"
                    onChange={(e: any) => setBundlesOnPos(e)}
                    options={posBundleServerEnabledOptions}
                    checkedText={posBundleServerEnabledOptions.check}
                    uncheckedText={posBundleServerEnabledOptions.uncheck}
                    value={bundlesOnPos}
                    disabled={isBundlesOnPosDisabled()}
                />
            </td>
            {isDesktop && <td>
            <div className="new-device-version__container__row">
              Mark the version to use the POS as a bundle server for this version of Game Terminal bundles. POS will download marked versions regardless of 'Valid Through' or 'Allow device activation'. It requires:
              The 'Device Version' must be fully entered, 3 or more digit version: '50.1.2' or '50.1.2.3' instead of only '50'.
              The 'Type' must be Game.
              Will only take effect on locations with the flag 'POS Bundle Server' enabled.
            </div>
          </td>}
          </tr>
          <tr>
            <td width={"25%"}>
              <Radio
                  label='Allow device activation'
                  options={activationOptions}
                  value={form.deviceActivationEnabled}
                  onChange={(e: any) => setForm({...form, deviceActivationEnabled: e.target.value})}
              />
            </td>
            {isDesktop && <td>
              General switch to Enable/Disable activation with this version. If ‘Yes,’ the ‘Valid Through’ date will be verified. If ‘No,’ the ‘Valid Through’ date is ignored and the activation is rejected.
            </td>}
          </tr>
          </tbody>
        </table>
        <div className="new-device-version__container__actions">
          <CancelButton />
          <Button type="primary"
                  onClick={() => submit()}
                  disabled={!isChanged()}
          >
            <p id="save-button">Save</p>
          </Button>
        </div>
      </div>
    </div>
  )
}

export default NewDeviceVersion
